import * as msal from "@azure/msal-browser";
import Identity from './IdentityModel';
import InteractiveSignInRequired from './MSALError';
import {config, loginRequest} from './msalAuthUtils';
import {InteractionRequiredAuthError} from "@azure/msal-browser";

/**
 * This service initializes the MSAL object,
 * makes the connection via AAD and provisions
 * methods to sign in, sign out and get ID.
 * */
class AuthService {
    constructor(configuration) {
        this.MsalAppplication = new msal.PublicClientApplication(configuration);
        this.MsalInitialized = false;
        console.log(`AuthService:: initialized: ${this.serviceName} is ready to use.`);
    }

    get serviceName() { return 'Marvel'; }

    returnUser = (loginResponse) => {
        const user = new Identity(loginResponse);
        this.accountObj = user.account;
        return user;
    }

    // strictly to be called from login button click
    // only other permitted usage is the last-in-line defense to attempt the refresh of token coming from the
    // authenticateUser method.
    async signIn() {
        if (!this.MsalInitialized) {
            console.log("Token Expired, Re-Initializing msalObj");
            await this.MsalAppplication.initialize().then(() => this.MsalInitialized = true);
        }
        try {
            console.log("Attempting SSO-Silent");
            const loginResponse = await this.MsalAppplication.ssoSilent(loginRequest);
            return this.returnUser(loginResponse);
        } catch (err) {
            if (err instanceof InteractionRequiredAuthError) {
                //use try/catch instead of .catch() so errors are not swallowed
                console.log("SSO-Silent Failed, Attempting a Popup Login");
                try {
                    const loginResponse = await this.MsalAppplication.loginPopup(loginRequest);
                    return this.returnUser(loginResponse);
                } catch (popupError) {
                    console.log("Popup Login failed:", popupError);
                    throw popupError;
                }
            } else {
                //rethrow instead of silently returning undefined
                console.log("Unexpected error during sign-in:", err);
                throw err;
            }
        }
    }

    signOut = () => {
        this.MsalAppplication.logoutPopup().then(() => this.MsalInitialized = false)
    }

    async authenticateUser(currentAccount) {
        if (!this.MsalInitialized) {
            console.log("Token Expired, Re-Initializing msalObj");
            await this.MsalAppplication.initialize().then(() => this.MsalInitialized = true);
        }
        const silentRequest = {
            scopes: ["openid", "profile", "user.read"],
            account: currentAccount,
            forceRefresh: false
        };
        if (currentAccount) {
            // this block should ideally be hit everytime we require a token to access API
            try {
                return await this.MsalAppplication.acquireTokenSilent(silentRequest);
            } catch (err) {
                console.log(err);
                if (err instanceof InteractionRequiredAuthError) {
                    console.log("Token Silent Failed, Attempting acquiring token using popup");
                    try {
                        const popupRequest = await this.MsalAppplication.acquireTokenPopup(loginRequest);
                        this.accountObj = popupRequest.account;
                        return popupRequest;
                    } catch (error) {
                        console.log("All Attempts to refresh the token have failed, now attempting to sign-in with a popup-login window.");
                        return await this.signIn();
                    }
                } else {
                    //rethrow non-interaction errors instead of silently returning undefined
                    console.log("Unexpected error during token acquisition:", err);
                    throw err;
                }
            }
        } else {
            console.log("Current Account not found, attempting to sign-in with a popup-login window.")
            return await this.signIn();
        }
    }

    async idUser() {
        if (this.accountObj) {
            return await this.authenticateUser(this.accountObj)
        } else {
            const currentAccounts = this.MsalAppplication.getAllAccounts();
            if (!currentAccounts || currentAccounts.length === 0) {
                throw new InteractiveSignInRequired();
            } else if (currentAccounts.length > 1) {
                //multiple accounts found — use the first one as default
                // rather than leaving this.accountObj undefined and silently failing downstream
                console.warn("Multiple accounts detected, defaulting to first account:", currentAccounts[0].username);
                this.accountObj = currentAccounts[0];
            } else {
                this.accountObj = currentAccounts[0];
            }
            return await this.authenticateUser(this.accountObj)
        }
    }
}

const authService = new AuthService(config);

export default authService;
